import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import Button from '../../../../components/ui/Button';

interface ConfirmSwapDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  submitting?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Lightweight confirmation for the swap actions that mutate a colleague's
 * request (Decline someone else's request, Cancel your own). Keeps the
 * consequence-bearing actions consistent with Accept/Approve, which already
 * confirm, so no single misclick reverses a swap.
 */
const ConfirmSwapDialog: React.FC<ConfirmSwapDialogProps> = ({
  open,
  title,
  description,
  confirmLabel,
  destructive = true,
  submitting = false,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !submitting) onClose(); }}>
      <DialogContent
        size="sm"
        onEscapeKeyDown={(e) => { if (submitting) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (submitting) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" size="sm" disabled={submitting} onClick={onClose}>
            {t('Back')}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            size="sm"
            loading={submitting}
            onClick={onConfirm}
            data-testid="swap-confirm-action"
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmSwapDialog;
